// What a task of a given `TaskOrigin` implies when its producer says nothing —
// one total `Record` per fact. See `../task.ts` for the file docblock.

import { TaskOrigin, TaskOriginSchema } from '../../value-objects/task-envelope';
import { OutcomeKind } from './outcome';

/**
 * What a task of this ORIGIN DELIVERS, when its producer says nothing.
 *
 * The SECOND origin-keyed record, beside {@link ORIGIN_EDITS_REPO}, and it exists
 * because `tasks.outcome_kind` lied about every non-coding lane for the column's
 * whole life. The column is `notNull().default('pr')` (`packages/db/src/schema.ts`)
 * and almost nothing stamped it — not `plan_start` (`apps/api/src/deps/plan-start.ts`),
 * not the scout backlog drain (`apps/worker/src/cron/scout-backlog-drain.ts`), not
 * the review / review-reply / babysit minters (`apps/worker/src/review/`,
 * `apps/worker/src/babysit/`). Prod on 2026-08-26, all-time, one row per
 * `(origin, outcome_kind)`:
 *
 * ```
 *  pr_babysit 1624 pr | pr_review_reply 1187 pr | pr_review 1125 pr
 *  pr_babysit_watch 838 pr | manual 573 pr | scout 495 pr | conversation 27 pr
 *  manual 24 report   ← the ONE stamping producer: the gardener (#456)
 * ```
 *
 * 495 of 495 scout sessions therefore claimed to deliver a pull request, and a
 * scout session only ever authors a plan. The user-visible cost is on the
 * reporter's own issue thread: `renderTaskNarration`
 * (`apps/worker/src/fleet/task-narration.ts`) reads this column, so a finished
 * scout publicly said *"Finished - no pull request is linked to this run"* and its
 * `Done` box never ticked. The already-recorded cost is the repo-onboarding
 * incident in {@link ORIGIN_EDITS_REPO} below.
 *
 * A `Record<TaskOrigin, OutcomeKind>` and not a `Map`, a `Set` or a switch, for the
 * SAME reason its sibling is one: adding an origin FAILS THE BUILD until someone
 * says what it delivers. That forcing function is the whole point — the defect
 * above was an omitted field nobody was asked about.
 *
 * **A DEFAULT, applied at the ONE write seam, never at the producers.** It is folded
 * in by the three `tasks` INSERTs (`createTask` / `createTaskIfAbsent` /
 * `ensureBabysitWatchTask`, `packages/db/src/queries/tasks.ts`), exactly where
 * `withRequiredCapabilities` folds in the other origin-derived fact and for the
 * reason stated there: a rule enforced in the producers is a rule the next producer
 * walks past. There are ten of them across four apps, `apps/agents-manager` and
 * `packages/agent`'s `directSink` included, and the gate's own comment already asked
 * for this ("it has to be done at every producer, and the gate must not be the thing
 * that discovers a new one forgot"). An EXPLICIT `outcomeKind` still wins, which is
 * what keeps the gardener's 24 `report` rows and the `ops` verb's `ops_action`.
 *
 * **It is NOT `ORIGIN_EDITS_REPO` negated**, and `pr_babysit_watch` is where the two
 * part company. That origin commits nothing (it is a born-`completed` bookkeeping
 * anchor, so `ORIGIN_EDITS_REPO` says `false`), but what it DELIVERS is a pull
 * request: it carries `pull_request_number`, the babysit driver walks it
 * `completed → awaiting_ci → … → merged`, and the narration's `pr` arm says
 * *"Work landed on … — watching it toward merge"* about it, correctly. Calling it
 * `report` would tick `Done` on a PR still being watched and promise a report that
 * does not exist. Two facts, two records.
 *
 * **`scout` is `report`, and deliberately NOT a new `plan` member.** `OutcomeKind` is
 * a stored value in a `notNull` column across 3 800+ live rows, so adding a member is
 * a one-way door. `report` is defined as "delivers a markdown report + artifacts into
 * run events (no PR)", which is a scout's observable outcome — the plan lands in
 * `plan_authored` run events and `plan_groups` rows. Nothing in the repo
 * branches on plan-vs-report; the day something must, that is when the member is
 * earned.
 */
export const OUTCOME_KIND_FOR_ORIGIN: Record<TaskOrigin, OutcomeKind> = {
  [TaskOrigin.Conversation]: OutcomeKind.Pr,
  [TaskOrigin.GhIssue]: OutcomeKind.Pr,
  [TaskOrigin.Sentry]: OutcomeKind.Pr,
  [TaskOrigin.Linear]: OutcomeKind.Pr,
  /** Plain coding work by default; the gardener's maintenance lane stamps `report` itself (#456). */
  [TaskOrigin.Manual]: OutcomeKind.Pr,
  /** Posts a review on someone else's PR and never pushes — the verdict is the deliverable. */
  [TaskOrigin.PrReview]: OutcomeKind.Report,
  /** Replies on review threads, resolves them, stores KB. No commits, no PR. */
  [TaskOrigin.PrReviewReply]: OutcomeKind.Report,
  /** Fixes red CI / addresses comments ON a fleet PR — it pushes, and that PR is the outcome. */
  [TaskOrigin.PrBabysit]: OutcomeKind.Pr,
  /** The non-fleet PR's watch anchor: it authors nothing, but a PR is what it delivers — see above. */
  [TaskOrigin.PrBabysitWatch]: OutcomeKind.Pr,
  /** An interactive planning session: it authors a plan into run events, never a PR. */
  [TaskOrigin.Scout]: OutcomeKind.Report,
  /** The onboarding pass — it opens a `dz/onboarding` PR. */
  [TaskOrigin.Setup]: OutcomeKind.Pr,
  /**
   * The FIX, as a pull request, by default (owner 2026-11-03, "give POWER"; the runner's
   * `DEVOPS_DEFAULT_OUTCOME` says the same). A findings-only `report` or an infrastructure
   * `ops_action` is stamped explicitly by its producer (the `ops` verb names its lane).
   */
  [TaskOrigin.Devops]: OutcomeKind.Pr,
  /**
   * One model call offered to a machine (#6759): it delivers a result into run events, no
   * PR. `scout`'s reason for not minting a new `OutcomeKind` member applies verbatim.
   */
  [TaskOrigin.TriageJudge]: OutcomeKind.Report,
  /** The KB fold's one call, offered the same way (#6759). */
  [TaskOrigin.KbFold]: OutcomeKind.Report,
  /** The Planner's plan call (#7569), offered the same way: it delivers a report. */
  [TaskOrigin.PlanAuthor]: OutcomeKind.Report,
};

/**
 * Does a run of this ORIGIN edit the repository it names — i.e. does it end in
 * commits and a pull request?
 *
 * A SECOND fact from {@link OutcomeKind}, and it earned its existence when the two
 * disagreed in production. `tasks.outcome_kind` is `notNull().default('pr')`
 * (`packages/db/src/schema.ts`) and, until {@link OUTCOME_KIND_FOR_ORIGIN} above,
 * almost no producer stamped it: neither `plan_start`
 * (`apps/api/src/deps/plan-start.ts`) nor the scout backlog drain
 * (`apps/worker/src/cron/scout-backlog-drain.ts`) nor the review / babysit minters
 * passed `outcomeKind` to `createTask`. So the column said `pr` for a scout session
 * that only ever authors a plan.
 * Any gate that asks "is this row PR-opening work?" off the COLUMN therefore got
 * `true` for every lane, and the repo-onboarding gate did exactly that: it held
 * every `plan_start` on an un-onboarded repo forever, while `plan_start` answered
 * 202 and `task run_status` answered `run_id: null` for as long as anyone polled.
 *
 * The column is stamped at the write seam now, so that reading is fixed at the
 * source — but this record STAYS, and the gate keeps reading it. The two answer
 * different questions ("does it commit?" vs "what does it deliver?") and
 * `pr_babysit_watch` is where they part company; and the column is still writable
 * after mint (`applyReportOutcome`), while an envelope's origin is not.
 *
 * A `Record<TaskOrigin, boolean>` and not a `Set` or a switch: adding an origin
 * FAILS THE BUILD until someone says which side it is on. That forcing function is
 * the whole point — the defect above was an omitted field nobody was asked about.
 *
 * `setup` is `true` (the onboarding pass opens a `dz/onboarding` PR); it is exempt
 * from the onboarding gate for a DIFFERENT reason — it is the thing that clears the
 * gate — and that exemption stays its own named conjunct rather than hiding here.
 */
export const ORIGIN_EDITS_REPO: Record<TaskOrigin, boolean> = {
  [TaskOrigin.Conversation]: true,
  [TaskOrigin.GhIssue]: true,
  [TaskOrigin.Sentry]: true,
  [TaskOrigin.Linear]: true,
  [TaskOrigin.Manual]: true,
  /** Posts a review on someone else's PR; it never pushes to the repo. */
  [TaskOrigin.PrReview]: false,
  /** Replies on review threads, resolves them, stores KB. No commits. */
  [TaskOrigin.PrReviewReply]: false,
  /** Fixes red CI / addresses comments ON a fleet PR — commits and pushes. */
  [TaskOrigin.PrBabysit]: true,
  /** A born-`completed` bookkeeping anchor for a non-fleet PR. Runs nothing. */
  [TaskOrigin.PrBabysitWatch]: false,
  /** An interactive planning session: it authors a plan (`plan_authored`), not a PR. */
  [TaskOrigin.Scout]: false,
  /** The onboarding pass itself — it DOES open a PR; see the note above. */
  [TaskOrigin.Setup]: true,
  /**
   * Its default outcome is `pr` (the fix), which commits. A `report` / `ops_action` devops
   * row is non-editing by its explicit stamp (`areaEditsRepoSql`, `@developerz/db`).
   */
  [TaskOrigin.Devops]: true,
  /** One model call (#6759): no checkout, no diff. */
  [TaskOrigin.TriageJudge]: false,
  /** One model call (#6759): no checkout, no diff. */
  [TaskOrigin.KbFold]: false,
  [TaskOrigin.PlanAuthor]: false,
};

/**
 * Does a claim of this origin run the TEAM lane's survey-then-edit session, and so take
 * its area lock on its PLANNED set (`files_plan`) before any editor writes? The set a
 * claim may admit FOR PLANNING without its declared area lock (#5370,
 * `planAdmissionSql`, `@developerz/db`) — only a lane that locks its plan before it
 * edits may be admitted without one. (The claim also requires `outcome_kind = 'pr'`: a
 * `report` / `ops_action` outcome runs the single adapter, which takes no plan lock.)
 *
 * `false` for every lane that edits without that survey: a `pr_babysit` round (its lock
 * is its PR's diff, a producer of its own), the `setup` onboarding pass (its own mode),
 * and every non-editing origin. A `Record` for the reason {@link ORIGIN_EDITS_REPO} is
 * one: a new origin fails the build until someone says which side it is on.
 */
export const ORIGIN_PLANS_BEFORE_EDIT: Record<TaskOrigin, boolean> = {
  [TaskOrigin.Conversation]: true,
  [TaskOrigin.GhIssue]: true,
  [TaskOrigin.Sentry]: true,
  [TaskOrigin.Linear]: true,
  [TaskOrigin.Manual]: true,
  [TaskOrigin.PrReview]: false,
  [TaskOrigin.PrReviewReply]: false,
  [TaskOrigin.PrBabysit]: false,
  [TaskOrigin.PrBabysitWatch]: false,
  [TaskOrigin.Scout]: false,
  [TaskOrigin.Setup]: false,
  [TaskOrigin.Devops]: false,
  [TaskOrigin.TriageJudge]: false,
  [TaskOrigin.KbFold]: false,
  [TaskOrigin.PlanAuthor]: false,
};

/**
 * Can a run of this ORIGIN be PAUSED (#6747, epic #6552)? A pause stops the run at a step
 * boundary and keeps what it saved (the `Dz-Wip-Task` save) for Resume on the same
 * machine, and only the team session writes that save: it is the `pr` outcome's coding
 * lane (`apps/runner/src/modes/worker/runtime.ts`). So `true` for exactly the five
 * origins whose `pr` runs go through it; `false` for every other lane, including the
 * one-model-call origins of #6759 (`triage_judge`, `kb_fold`) and #7569 (`plan_author`):
 * no checkout, no save.
 * Today the `true` rows equal {@link ORIGIN_PLANS_BEFORE_EDIT}'s, but the facts differ
 * and each is decided on its own. Spec default (not an owner ruling): only the coding
 * lane that saves work can pause. The runner reads it through {@link isPausableTask}
 * (`apps/runner/src/cancel.ts` `CancelBus.pause`, #6749), and so does the pause door
 * (`apps/api/src/deps/task-pause.ts`, #6751). A `Record` for the reason
 * {@link ORIGIN_EDITS_REPO} is one: a new origin fails the build until someone decides.
 */
export const ORIGIN_RUN_PAUSES: Record<TaskOrigin, boolean> = {
  [TaskOrigin.Conversation]: true,
  [TaskOrigin.GhIssue]: true,
  [TaskOrigin.Sentry]: true,
  [TaskOrigin.Linear]: true,
  [TaskOrigin.Manual]: true,
  [TaskOrigin.PrReview]: false,
  [TaskOrigin.PrReviewReply]: false,
  [TaskOrigin.PrBabysit]: false,
  [TaskOrigin.PrBabysitWatch]: false,
  [TaskOrigin.Scout]: false,
  [TaskOrigin.Setup]: false,
  [TaskOrigin.Devops]: false,
  [TaskOrigin.TriageJudge]: false,
  [TaskOrigin.KbFold]: false,
  [TaskOrigin.PlanAuthor]: false,
};

/** Can this task be paused: a pausable origin ({@link ORIGIN_RUN_PAUSES}) running its `pr` outcome. Pure. */
export function isPausableTask(task: { origin: TaskOrigin; outcomeKind: OutcomeKind }): boolean {
  return ORIGIN_RUN_PAUSES[task.origin] && task.outcomeKind === OutcomeKind.Pr;
}

/**
 * Can a run of this ORIGIN author a plan keyed on its RUN id (`plans.id = run_id`)? The
 * box emits `plan_authored` from a planning session — a `scout` and the `setup` pass
 * (whose follow-ups ride the same event, `stampFromAnchor` in
 * `apps/worker/src/fleet/scout-plan-ingest.ts`) — and the ingest mints the plan under
 * the run id. Every other origin's run authors none, so a missing plan on it is the
 * expected answer, not a fault (`resolveTaskOutcomePlanAbsence`, `./outcome-view`). A setup pass's
 * SCAFFOLD plan is keyed on its TASK id (`buildScaffoldPlan`) and is not this fact.
 *
 * Measured prod 2026-10-19, `completed` tasks of the last 30 days: every row with a
 * run-keyed plan was `scout` or `setup`. A `Record` for the reason
 * {@link ORIGIN_EDITS_REPO} is one.
 */
export const ORIGIN_RUN_AUTHORS_PLAN: Record<TaskOrigin, boolean> = {
  [TaskOrigin.Conversation]: false,
  [TaskOrigin.GhIssue]: false,
  [TaskOrigin.Sentry]: false,
  [TaskOrigin.Linear]: false,
  [TaskOrigin.Manual]: false,
  [TaskOrigin.PrReview]: false,
  [TaskOrigin.PrReviewReply]: false,
  [TaskOrigin.PrBabysit]: false,
  [TaskOrigin.PrBabysitWatch]: false,
  [TaskOrigin.Scout]: true,
  [TaskOrigin.Setup]: true,
  [TaskOrigin.Devops]: false,
  [TaskOrigin.TriageJudge]: false,
  [TaskOrigin.KbFold]: false,
  [TaskOrigin.PlanAuthor]: false,
};

/** The origins of {@link ORIGIN_PLANS_BEFORE_EDIT}, derived so the list cannot drift from it. */
export const PLAN_ADMISSION_ORIGINS: readonly TaskOrigin[] = TaskOriginSchema.options.filter(
  (origin) => ORIGIN_PLANS_BEFORE_EDIT[origin],
);

/**
 * The origins whose runs never touch the repo — DERIVED from
 * {@link ORIGIN_EDITS_REPO} so the list cannot drift from the record that has to
 * be exhaustive. Consumed by the repo-onboarding gate
 * (`repoNotOnboardedSql`, `packages/db/src/queries/tasks/gates.ts`) and its read
 * (`onboarding-owed.ts`), the same-area gate (`areaEditsRepoSql`, `tasks/area-gate.ts`),
 * which must not hold work that was never going to edit the repo.
 *
 * `devops` IS NOT HERE (owner 2026-11-03, "give POWER"): a devops run's default deliverable
 * is the FIX, a pull request (`OUTCOME_KIND_FOR_ORIGIN[devops] = pr`, the runner's
 * `DEVOPS_DEFAULT_OUTCOME`), which commits — so a defaulted devops row is held by the
 * onboarding gate and the area gate exactly like `manual` work, and search dedup and the
 * board treat its completed `pr` row as coding work in motion until the PR lands. A devops
 * run that only reports or acts on infrastructure is stamped `report` / `ops_action` by its
 * producer, and THAT stamp — never the origin — makes it non-editing on every reader
 * (`areaEditsRepoSql`, the gate's `outcome_kind = 'pr'` conjunct, the finished-completed
 * outcome arm). The claim gate and its reads derive from this one list, so they cannot
 * disagree about it.
 */
export const NON_EDITING_ORIGINS: readonly TaskOrigin[] = TaskOriginSchema.options.filter(
  (origin) => !ORIGIN_EDITS_REPO[origin],
);

/**
 * Does a task of this ORIGIN work ON a pull request ANOTHER task owns — and if so, which
 * envelope spec names that PR (`envelope[key].pr_number`)? `null` = the task is its own
 * deliverable.
 *
 * WHY IT IS A DEPENDENCY FACT. A `depends_on` edge releases when its upstream MERGED or
 * finished with no PR (`taskReleasesDependentsSql`). A PR chore does neither: a babysit
 * round pushes onto its PR's branch and its row never carries the PR (prod, 30 days to
 * 2026-09-30: 3 001 `pr_babysit` rows, 0 with a `pull_request_number`, 0 `merged`), and a
 * review or a review reply is superseded by the next round and cancelled. So an edge on a
 * chore could only hold forever (a `completed` babysit, `pr_unfulfilled`) or fail its
 * dependent (a cancelled one) — all 18 such edges prod ever held ended one of those two
 * ways. What the writer meant is the PR the chore serviced: the dependency sweep rewrites
 * the edge to that PR's OWNING task (`healSupersededEdges`, `@developerz/db`), the way it
 * rewrites an edge on a superseded task to its replacements.
 *
 * A `Record` for the reason {@link ORIGIN_EDITS_REPO} is one: a new origin fails the build
 * until someone says whether it owns its outcome. `pr_babysit_watch` is NOT a chore — it
 * carries its PR's number and the babysit driver walks it to `merged`.
 */
export const ORIGIN_PR_CHORE_SPEC: Record<
  TaskOrigin,
  'babysit' | 'review' | 'review_reply' | null
> = {
  [TaskOrigin.Conversation]: null,
  [TaskOrigin.GhIssue]: null,
  [TaskOrigin.Sentry]: null,
  [TaskOrigin.Linear]: null,
  [TaskOrigin.Manual]: null,
  [TaskOrigin.PrReview]: 'review',
  [TaskOrigin.PrReviewReply]: 'review_reply',
  [TaskOrigin.PrBabysit]: 'babysit',
  [TaskOrigin.PrBabysitWatch]: null,
  [TaskOrigin.Scout]: null,
  [TaskOrigin.Setup]: null,
  [TaskOrigin.Devops]: null,
  [TaskOrigin.TriageJudge]: null,
  [TaskOrigin.KbFold]: null,
  [TaskOrigin.PlanAuthor]: null,
};

/** The origins of {@link ORIGIN_PR_CHORE_SPEC} that name a spec, derived so the list cannot drift. */
export const PR_CHORE_ORIGINS: readonly TaskOrigin[] = TaskOriginSchema.options.filter(
  (origin) => ORIGIN_PR_CHORE_SPEC[origin] !== null,
);

/**
 * Does the lane this ORIGIN routes to READ the assignment's sealed
 * `llm_credential`?
 *
 * The THIRD origin-keyed record, beside {@link OUTCOME_KIND_FOR_ORIGIN} and
 * {@link ORIGIN_EDITS_REPO}. It started as the answer to plan 101 slice 11 § gap B:
 * the mothership sealed and tier-stamped a BYOK credential onto every claim while the
 * coding lanes read none, so the claim acquired a pool key for a run that then billed
 * the box's own `DZ_BACKEND_*` env. Gap B was answered `false` for those lanes. Plan
 * 102 slice 07 (owner ruling A on #4678) answers it the other way, and flipping the
 * rows here is the dispatch half of that answer.
 *
 * THE ENUMERATION THAT MAKES THIS TRUE. A box routes a claimed assignment by
 * `envelope.origin`, through exactly two tables (`apps/runner/src/modes/router.ts`:
 * `routeInteractive` layered over `routeAutonomous`), to its executors. Every one
 * that runs a model resolves it from the sealed credential when one is sent:
 *
 * | executor    | origin              | credential |
 * |---|---|---|
 * | `scout`     | `scout`             | `modes/scout/brain.ts` → `resolveAssignmentModel` |
 * | `setup`     | `setup`             | `modes/onboarding/index.ts` → `resolveAssignmentModel` |
 * | `review`    | `pr_review`         | `modes/worker/review-brain.ts` → `resolveAssignmentModel` |
 * | `threads`   | `pr_review_reply`   | `modes/worker/review-threads.ts` reads `assignment.llm_credential` |
 * | `babysit`   | `pr_babysit`        | the coding adapter → `harness/lane-model.ts` `laneModelFor` |
 * | `devops`    | `devops`            | the outcome router → `laneModelFor` (survey fan-out too) |
 * | `outcome`   | everything else     | the coding / team lane → `laneModelFor` |
 *
 * The last three are the HARNESS lanes. Since dz-runner 0.0.17 (#4697, slice 07a)
 * `laneModelFor` builds their model from the sealed credential and falls back to the
 * box env ONLY when the assignment carries none. It never falls back from a credential
 * it cannot open; that throws. So `true` here means two things on the mothership, and
 * both are enforced where the assignment is built (`claimAndOutbox`,
 * `apps/worker/src/dispatch/assigner.ts`), not here:
 *
 * - For the harness lanes ({@link ORIGIN_CREDENTIAL_NEEDS_CODING_RUNNER}) the
 *   credential goes only to a box whose build uses it and reports the key it failed
 *   on (`runnerSealsCodingCredential`, 0.0.18). An older box runs these lanes on its
 *   own env: a credential sent there would record a pool key that nothing billed,
 *   and a pool-key 429 it could not name would pause the account's backend.
 * - The dispatch outcomes (seal, seal on the pin, cooling hold, box env) are
 *   in `docs/plans/2026/09/21/102-byok-model-pool-quota/07-coding-lane-pool.md`
 *   § Dispatch contract.
 *
 * `triage_judge` / `kb_fold` (#6759) are `true`: the runner's model-call executor (#6760)
 * makes its one call on the credential, which the claim seals bound to the job's
 * `model_pin`. Only a box holding `harness:model-call` is offered one.
 *
 * `false` has ONE row: `pr_babysit_watch`, which is born `completed`, is never
 * claimed, and so never reaches a lane. The scout's survey passes are a known gap. A
 * survey still runs on the box env even when the scout carries a credential
 * (`apps/runner/src/modes/scout/index.ts`). That is deferred as F6 in the plan above.
 * The row stays `true` because the scout's own brain does read the credential.
 *
 * A `Record<TaskOrigin, boolean>` and not a `Set` or a switch, for the same reason
 * its two siblings are: adding an origin FAILS THE BUILD until someone says which
 * side it is on. Consumed by `claimAndOutbox`, the one seam that builds an
 * assignment. Pinned against the box's own routing table by
 * `apps/runner/src/modes/router.test.ts`, so the two halves of the contract cannot
 * drift apart.
 */
export const ORIGIN_READS_LLM_CREDENTIAL: Record<TaskOrigin, boolean> = {
  /** The coding / team lane builds its harness model through `laneModelFor` (runner 0.0.17). */
  [TaskOrigin.Conversation]: true,
  [TaskOrigin.GhIssue]: true,
  [TaskOrigin.Sentry]: true,
  [TaskOrigin.Linear]: true,
  [TaskOrigin.Manual]: true,
  /** The reviewer's verdict is authored by an in-process brain on the account's key. */
  [TaskOrigin.PrReview]: true,
  /** Thread replies are authored by the same brain, from the same sealed key. */
  [TaskOrigin.PrReviewReply]: true,
  /** Fixes red CI through the coding adapter, whose model is `laneModelFor`'s (runner 0.0.17). */
  [TaskOrigin.PrBabysit]: true,
  /** Born `completed`, so the assigner never claims it and no credential is ever resolved. */
  [TaskOrigin.PrBabysitWatch]: false,
  /** The planning session converses and authors its plan in process. */
  [TaskOrigin.Scout]: true,
  /** The onboarding session likewise — `setup/base.ts` refuses the call outright without one. */
  [TaskOrigin.Setup]: true,
  /** Reproduces and deploys through the outcome router and its survey fan-out, both on `laneModelFor` (runner 0.0.17). */
  [TaskOrigin.Devops]: true,
  /** The sealed credential IS the call (#6759): the machine makes one call on it, in process. */
  [TaskOrigin.TriageJudge]: true,
  /** Likewise the KB fold's call (#6759). */
  [TaskOrigin.KbFold]: true,
  /** Likewise the Planner's plan call (#7569): the sealed key the cluster picked. */
  [TaskOrigin.PlanAuthor]: true,
};

/**
 * Which credential-reading origins need a box on the coding-credential runner build
 * before the assigner seals a credential for them (plan 102 slice 07b). A STRICT
 * SUBSET of {@link ORIGIN_READS_LLM_CREDENTIAL}: the harness lanes (the coding / team
 * lane, babysit, devops). Builds before 0.0.17 ignored the credential on these lanes
 * and ran on the box env, and builds before 0.0.18 cannot name the failing pool key on
 * an error event. The version test is `runnerSealsCodingCredential`
 * (`../runner/box-kek.ts`); the claim that applies it is `claimAndOutbox`
 * (`apps/worker/src/dispatch/assigner.ts`).
 *
 * `false` on the brain lanes, and that is load-bearing: review, reply, scout and
 * setup have opened the sealed credential since the per-box KEK (0.0.4),
 * so gating them on the coding build would starve review and planning work on every
 * older box. `false` on `pr_babysit_watch`, which reads no credential at all.
 */
export const ORIGIN_CREDENTIAL_NEEDS_CODING_RUNNER: Record<TaskOrigin, boolean> = {
  [TaskOrigin.Conversation]: true,
  [TaskOrigin.GhIssue]: true,
  [TaskOrigin.Sentry]: true,
  [TaskOrigin.Linear]: true,
  [TaskOrigin.Manual]: true,
  [TaskOrigin.PrReview]: false,
  [TaskOrigin.PrReviewReply]: false,
  [TaskOrigin.PrBabysit]: true,
  [TaskOrigin.PrBabysitWatch]: false,
  [TaskOrigin.Scout]: false,
  [TaskOrigin.Setup]: false,
  [TaskOrigin.Devops]: true,
  /** Gated by `harness:model-call` instead (`requiredCapabilitiesForOrigin`, #6759). */
  [TaskOrigin.TriageJudge]: false,
  /** Gated by `harness:model-call` instead (#6759). */
  [TaskOrigin.KbFold]: false,
  [TaskOrigin.PlanAuthor]: false,
};

/**
 * Which origins' lanes read {@link FleetTaskAssignmentSchema}'s
 * `llm_credential_fallback` (#2203) — a STRICT SUBSET of
 * {@link ORIGIN_READS_LLM_CREDENTIAL}.
 *
 * The sibling credential exists for ONE consumer: the review ensemble's bounded
 * top-up (`apps/runner/src/modes/worker/review-brain.ts`), reached only through
 * `bunResolveModelBrain`. Every other credential-reading lane — the reply
 * threads, the scout's planning session, the setup session — builds its model
 * elsewhere and would never open it. So this is not a copy of its sibling with
 * the same values: shipping a second sealed BYOK key to a box that cannot read
 * it is key exposure with no consumer, against the assignment's own repeated
 * "omitted so the wire stays lean" posture.
 *
 * It lives BESIDE its sibling, and is read at the same seam (`buildAssignment`'s
 * caller), because the invariant is a property of the ASSIGNMENT — "it carries a
 * fallback iff its lane reads one" — exactly as the credential gate's own
 * comment argues.
 */
export const ORIGIN_READS_LLM_FALLBACK: Record<TaskOrigin, boolean> = {
  [TaskOrigin.Conversation]: false,
  [TaskOrigin.GhIssue]: false,
  [TaskOrigin.Sentry]: false,
  [TaskOrigin.Linear]: false,
  [TaskOrigin.Manual]: false,
  /** The ONE lane: the review ensemble's top-up dials it (#2203). */
  [TaskOrigin.PrReview]: true,
  /**
   * NOT the reply lane. `isReviewReplyAssignment` routes it to
   * `review-threads.ts`, which never calls `bunResolveModelBrain` — it would
   * receive a key it has no code to open.
   */
  [TaskOrigin.PrReviewReply]: false,
  [TaskOrigin.PrBabysit]: false,
  [TaskOrigin.PrBabysitWatch]: false,
  /** The scout converses in process on the primary; it runs no ensemble. */
  [TaskOrigin.Scout]: false,
  /** Likewise the onboarding session. */
  [TaskOrigin.Setup]: false,
  [TaskOrigin.Devops]: false,
  /** The seal is bound to the cluster's exact pick (`model_call_jobs.model_pin`): no failover key. */
  [TaskOrigin.TriageJudge]: false,
  /** Likewise (#6759). */
  [TaskOrigin.KbFold]: false,
  [TaskOrigin.PlanAuthor]: false,
};

/**
 * Which origins' lanes read {@link FleetTaskAssignmentSchema}'s
 * `conversation_digest` (plan 101 doc 06 step 3) — the prior turns of the
 * envelope's `conversation_ref`, recalled so a later session continues one
 * conversation instead of starting a new one.
 *
 * The gate is the same shape and the same argument as
 * {@link ORIGIN_READS_LLM_CREDENTIAL} beside it — "an assignment carries a field
 * iff its lane reads one" is a property of the ASSIGNMENT, so it is declared here
 * and applied at `buildAssignment`'s caller, where no injected resolver can
 * bypass it. What it prevents is narrower than key exposure but real: a
 * `conversation_ref` is a top-level field on a `POST /v1/tasks` envelope / `task reassign`
 * too (prod carries 19 `manual`-origin tasks with one), and those lanes spawn the
 * coding adapter with the box's own env — they would be shipped a transcript no
 * code on that path renders, and pay a claim-time query per task to build it.
 *
 * TRUE for the conversational planning lane, the scout, and nothing else: it
 * renders the digest in its plan prompt (`buildScoutUserPrompt`, `apps/runner`).
 */
export const ORIGIN_READS_CONVERSATION_DIGEST: Record<TaskOrigin, boolean> = {
  [TaskOrigin.Conversation]: false,
  [TaskOrigin.GhIssue]: false,
  [TaskOrigin.Sentry]: false,
  [TaskOrigin.Linear]: false,
  /** `POST /v1/tasks` accepts a `conversation_ref`; the coding adapter renders nothing from it. */
  [TaskOrigin.Manual]: false,
  [TaskOrigin.PrReview]: false,
  [TaskOrigin.PrReviewReply]: false,
  [TaskOrigin.PrBabysit]: false,
  [TaskOrigin.PrBabysitWatch]: false,
  /** The planning session — the lane the recall exists for. */
  [TaskOrigin.Scout]: true,
  /**
   * NOT the onboarding session. `setup` establishes a repo's base from the repo
   * itself; it holds a mailbox but authors no plan from prior turns, and prod has
   * never minted one carrying a `conversation_ref`.
   */
  [TaskOrigin.Setup]: false,
  [TaskOrigin.Devops]: false,
  [TaskOrigin.TriageJudge]: false,
  [TaskOrigin.KbFold]: false,
  /** The offer's prompt is served whole by the caller; #6764 renders the scout's digest (#7569). */
  [TaskOrigin.PlanAuthor]: false,
};

/**
 * Which origins' lanes read {@link FleetTaskAssignmentSchema}'s `carry_over` — what the
 * earlier runs of the same task left on record, rendered into the brief of the next run.
 *
 * Same gate, same seam and same argument as {@link ORIGIN_READS_CONVERSATION_DIGEST}: "an
 * assignment carries a field iff its lane reads one" is a property of the ASSIGNMENT,
 * applied at `buildAssignment`'s caller where no injected resolver can bypass it. What it
 * prevents: a review lane re-claimed after a release would pay a claim-time read and ship
 * a history its in-process brain has no code to render.
 *
 * TRUE for every lane whose brief renders the task through `taskTextOf`
 * (`apps/runner/src/carry-over.ts` names each renderer): the coding lanes (the team
 * coordinator's survey brief, and the native brief every single-adapter run, babysit fix
 * round and devops run opens with), the scout's plan turn and the onboarding survey turn.
 * FALSE for the review and reply brains (they screen and render their own live inputs,
 * never the envelope), and for `pr_babysit_watch`, which is never claimed.
 */
export const ORIGIN_READS_CARRY_OVER: Record<TaskOrigin, boolean> = {
  [TaskOrigin.Conversation]: true,
  [TaskOrigin.GhIssue]: true,
  [TaskOrigin.Sentry]: true,
  [TaskOrigin.Linear]: true,
  [TaskOrigin.Manual]: true,
  [TaskOrigin.PrReview]: false,
  [TaskOrigin.PrReviewReply]: false,
  [TaskOrigin.PrBabysit]: true,
  [TaskOrigin.PrBabysitWatch]: false,
  [TaskOrigin.Scout]: true,
  [TaskOrigin.Setup]: true,
  [TaskOrigin.Devops]: true,
  /** The prompt arrives composed (#6759); no brief renders the envelope. */
  [TaskOrigin.TriageJudge]: false,
  [TaskOrigin.KbFold]: false,
  /** Likewise: #6764 renders the scout's carry-over mothership-side (#7569). */
  [TaskOrigin.PlanAuthor]: false,
};

/**
 * Which origins' lanes read {@link FleetTaskAssignmentSchema}'s `reasoning_effort`
 * (plan 101 slice 17) — the purpose route's thinking effort, honoured ONLY by the
 * box's harness loops (`runNativeHarness`, `runGroundingPass`) through
 * `apps/runner/src/harness/reasoning.ts`.
 *
 * Same gate, same seam and same argument as {@link ORIGIN_READS_CONVERSATION_DIGEST}:
 * "an assignment carries a field iff its lane reads one" is a property of the
 * ASSIGNMENT, applied at `buildAssignment`'s caller. What it prevents here: a review
 * task on a route with an effort would otherwise ship a field its in-process review
 * brain never reads — a trail naming a request that was never made.
 *
 * TRUE for every lane that runs a harness loop: the coding lane (every autonomous
 * origin that falls through to coding), the babysit fix loop, devops, the scout's
 * survey passes, and the onboarding agent. FALSE for the review and reply brains (each
 * an in-process call on the account's key, not a harness loop), and for
 * `pr_babysit_watch`, which is never claimed.
 */
export const ORIGIN_READS_REASONING_EFFORT: Record<TaskOrigin, boolean> = {
  [TaskOrigin.Conversation]: true,
  [TaskOrigin.GhIssue]: true,
  [TaskOrigin.Sentry]: true,
  [TaskOrigin.Linear]: true,
  [TaskOrigin.Manual]: true,
  /** The in-process review brain — no harness loop. */
  [TaskOrigin.PrReview]: false,
  /** The same brain, answering threads. */
  [TaskOrigin.PrReviewReply]: false,
  /** The fix loop drives `runNativeHarness`. */
  [TaskOrigin.PrBabysit]: true,
  /** Born `completed`; never claimed. */
  [TaskOrigin.PrBabysitWatch]: false,
  /** The planning brain is in-process, but its SURVEY scouts are grounding passes. */
  [TaskOrigin.Scout]: true,
  /** The onboarding agent drives `runNativeHarness` on the account's model. */
  [TaskOrigin.Setup]: true,
  /** The probe hive's grounding passes and its harness runs. */
  [TaskOrigin.Devops]: true,
  /** One in-process call, not a harness loop (#6759). */
  [TaskOrigin.TriageJudge]: false,
  /** One in-process call, not a harness loop (#6759). */
  [TaskOrigin.KbFold]: false,
  [TaskOrigin.PlanAuthor]: false,
};

/**
 * Which origins' lanes read {@link FleetTaskAssignmentSchema}'s `co_author` — the
 * human a fleet commit credits with a `Co-authored-by:` trailer
 * (`value-objects/co-author.ts`).
 *
 * TRUE exactly for the lanes that author a NEW pull request on someone's behalf: the
 * coding lane every autonomous origin falls through to. FALSE for every lane whose
 * commits are the platform's own upkeep, even when the pull request it touches was a
 * person's — the babysit fix loop (its commit is the bot's repair, and the PR's own
 * commit already credits the person), the onboarding pass (platform scaffolding) —
 * and for every lane that commits nothing.
 *
 * Same gate, same seam and same argument as {@link ORIGIN_READS_CONVERSATION_DIGEST}:
 * applied at `buildAssignment`'s caller, so the resolver is not even asked for a lane
 * that has no use for the answer.
 */
export const ORIGIN_READS_CO_AUTHOR: Record<TaskOrigin, boolean> = {
  [TaskOrigin.Conversation]: true,
  [TaskOrigin.GhIssue]: true,
  [TaskOrigin.Sentry]: true,
  [TaskOrigin.Linear]: true,
  [TaskOrigin.Manual]: true,
  /** Posts a review; commits nothing. */
  [TaskOrigin.PrReview]: false,
  /** Replies on threads; commits nothing. */
  [TaskOrigin.PrReviewReply]: false,
  /** The platform's repair commit on an existing PR — never a person's authorship. */
  [TaskOrigin.PrBabysit]: false,
  /** Born `completed`; never claimed. */
  [TaskOrigin.PrBabysitWatch]: false,
  /** Authors a plan, not a commit. */
  [TaskOrigin.Scout]: false,
  /** The onboarding scaffold — the platform's own base, not a person's change. */
  [TaskOrigin.Setup]: false,
  /** Reports and ops actions; no PR. */
  [TaskOrigin.Devops]: false,
  /** One model call; commits nothing (#6759). */
  [TaskOrigin.TriageJudge]: false,
  /** One model call; commits nothing (#6759). */
  [TaskOrigin.KbFold]: false,
  [TaskOrigin.PlanAuthor]: false,
};
